import { subscribeSessionExpiry } from './session-expiry';
import { SolMenu } from './SolMenu';
import type { MenuMaddesi } from './sol-menu-veri';
import './sol-menu.css';
import { TurBilgi } from './turBilgi/TurBilgi';
import { OtelTeklif } from './otelTeklif/OtelTeklif';
import { Belgeler } from './Belgeler';
import { HizliTeklif } from './hizliTeklif/HizliTeklif';
import { HIZLI_METIN } from './metin/hizliTeklif';
import { OTEL_TEKLIF_METIN } from './metin/otelTeklif';
import { TUR_BILGI_METIN } from './metin/turBilgi';
import { Yazismalar } from './Yazismalar';
import { YAZISMALAR_METIN } from './metin/yazismalar';
import { BildirimZili } from './Bildirim';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  ApiError,
  type CustomerDuplicateProbe,
  type CustomerList,
  type DashboardSectionIndex,
  type Me,
  type WorkspaceSummary,
} from './api';
import { SuppliersPage, UsersPage } from './Admin';
import { SalesWorkspace } from './Sales';
import { DashboardPanels, GirisYonlendirmesi } from './Dashboard';
import { WebSitesi } from './WebSitesi';
import { SiteSiparisOzeti } from './SiteSiparisOzeti';
import {
  Alan,
  AlanSatiri,
  Notices,
  OversightWorkflowPanel,
  WorkspaceNotices,
} from './Workflow';
import { CustomerDetailPage, OrderDetailPage } from './Detay';
import { OperationsWorkspace } from './Operations';
import { PazarlamaSekmeleri } from './PazarlamaSekmeleri';
import { IkPaneli } from './Ik';
import { FinanceWorkspace } from './FinanceWorkspace';
import { ProfilePage, SettingsPage } from './Hesap';
import { YapilandirmaSayfasi } from './Yapilandirma';
import { HataSiniri, HataTetikleyici } from './HataSiniri';
import logoAdresi from './mbd-logo.svg';
import { BOLUM_ADI, KABUK_METIN, ROL_CALISMA_ALANI } from './metin/kabuk';
import { HESAP_METIN } from './metin/hesap';
import { musteriDurumu, musteriTuru, ORTAK, para, rolAdi, tamSayi, YEREL } from './metin/ortak';
import { seridiIzle } from './serit';
import { PAROLA_KUR_YOLU, ParolaKurPage } from './ParolaKur';
import { baglantiTiklamasi, musteriKimligi, musteriYolu, siparisKimligi, useYol, YOLLAR, type Yol } from './yonlendirme';

/**
 * Ekranda çizilen hiçbir cümle bu dosyada durmuyor: hepsi `metin/kabuk.ts` ve `metin/ortak.ts`
 * içinde. Gerekçe ve desen `metin/ortak.ts` başındaki notta.
 */

function LoginForm({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const metin = KABUK_METIN.giris;

  return (
    <main className="login-page">
      <section className="login-intro" aria-labelledby="product-title">
        <span className="eyebrow">{KABUK_METIN.marka.serit}</span>
        <h1 id="product-title">{metin.baslik}</h1>
        <p>{metin.tarif}</p>
        {/*
          Rozet yalnız yerel `vite` geliştirme sunucusunda doğru: "dış servisler tanımlı değil"
          orada gerçek, derlenmiş her pakette (öndeğer ortamı, üretim dahil) değil. `import.meta.
          env.DEV` yalnız `npm run dev` ile true; `vite build` çıktısı nerede çalıştırılırsa
          çalıştırılsın false, o yüzden rozet derlenmiş pakette hiç çizilmiyor (Ürün Sahibi'nin
          bulgusu, 10.09.2026: bu satır sabitti, üretimde de "Yerel ortam" yazıyordu).
        */}
        {import.meta.env.DEV ? (
          <div className="truth-panel"><span className="status-dot" /> {metin.ortam}</div>
        ) : null}
      </section>
      <form
        className="login"
        data-testid="login-form"
        aria-busy={busy}
        onSubmit={async (event) => {
          event.preventDefault(); setBusy(true); setError(null);
          try { await api.login(email, password); onSignedIn(await api.me()); }
          catch (caught) { setError(caught instanceof ApiError ? caught.message : metin.hata); }
          finally { setBusy(false); }
        }}
      >
        <span className="eyebrow">{metin.ustBaslik}</span>
        <h2>{metin.formBaslik}</h2>
        <p className="muted">{metin.formTarif}</p>
        <label>{metin.eposta}<input data-testid="email" autoComplete="username" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
        <label>{metin.parola}<input data-testid="password" autoComplete="current-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
        {error ? <p className="error" role="alert" data-testid="login-error">{error}</p> : null}
        <button className="primary full" data-testid="submit" type="submit" disabled={busy}>{busy ? metin.gonderiliyor : metin.devam}</button>
      </form>
    </main>
  );
}

/**
 * Açılış sayfası.
 *
 * Yalnız kendi verisini çekiyor. Eskiden bu istek müşteri listesiyle **birlikte** yapılıyordu
 * ve dört pano bölümünün, dört akış panelinin, tedarikçilerin ve kullanıcıların verisi de aynı
 * anda iniyordu: ADMIN girişinde ölçülen `/api` isteği **38** idi. Sayfalara bölününce açılan
 * sayfa neyse yalnız onun ucu çağrılıyor.
 */
function OverviewPage({ me }: { me: Me }) {
  const [summary, setSummary] = useState<WorkspaceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const metin = KABUK_METIN.genelBakis;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setSummary(await api.workspace()); }
    catch (caught) { setError(caught instanceof ApiError ? caught.message : KABUK_METIN.calismaAlani.hata); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  return (
    <>
      {error ? <div className="error callout" role="alert" data-testid="workspace-error"><span>{error}</span><button type="button" onClick={() => void load()}>{ORTAK.yeniden}</button></div> : null}
      <section id="overview" className="content-section" aria-labelledby="overview-title">
        <div className="section-heading">
          <div><span className="eyebrow">{metin.ustBaslik}</span><h2 id="overview-title">{metin.baslik}</h2></div>
          {loading ? <span className="loading-chip">{ORTAK.yenileniyor}</span> : <span className="updated-chip">{metin.guncel}</span>}
        </div>
        {/*
          Kartın altındaki kaynak satırı duruyor ama artık sorgunun kendisini yazmıyor.
          Eskiden `Customer.count` yazıyordu; bir tablo adı operatöre hiçbir şey söylemiyor.
          Karşılığı uçtan geliyor (`api/src/modules/workspace/workspace.module.ts`).
        */}
        <div className="cards" data-testid="workspace-cards">
          {summary?.cards.map((card) => (
            <article className="card" key={card.key} data-testid={`card-${card.key}`}>
              <div className="card-label">{card.label}</div>
              <div className="value">{typeof card.value === 'number' ? tamSayi(card.value) : card.value}</div>
              <div className="source">{metin.kaynak} {card.source}</div>
            </article>
          ))}
          {loading && !summary ? Array.from({ length: 3 }).map((_, index) => <div className="card skeleton" key={index} />) : null}
        </div>
      </section>
      <SiteSiparisOzeti me={me} />
      <div className="workflow-stack"><WorkspaceNotices me={me} /></div>
    </>
  );
}

/**
 * Hızlı müşteri ekleme: satış akışı vakasının birinci adımı (`docs/crm/satis-akisi-vaka.md`).
 *
 * Ölçüldü, 26.08.2026: `customers.service.ts` içinde `create`, `findDuplicates`, `list`, `update`
 * hepsi yazılı ve `customer:create` izni tanımlıydı, ama hiçbir HTTP ucu açılmamıştı — ve panelde
 * bu ekran hiç yoktu. `/satis` (`Workflow.tsx`teki `SalesWorkflowPanel`) kendi müşteri formunu
 * taşıyor (tür seçici, kurumsal alanlar dahil, tam form) ama Müşteriler listesinde tek bir kayıt
 * yolu bile yoktu — Ürün Sahibi'nin "CRM'de müşteri ekleme yok" tespiti tam olarak burasıydı.
 *
 * Form yalnız bireysel müşteri açıyor (Ürün Sahibi'nin verdiği örnek bir telefon aramasıydı,
 * kurumsal kayıt zaten `/satis`te var). Dört alan: ad, soyad, telefon, e-posta (opsiyonel).
 *
 * Düzeltme (26.08.2026, Ürün Sahibi kararı): TC Kimlik No **zorunlu değil** — "TC numarası
 * vermeyebilir, zorunlu yapmasın." Form bu alanı beşinci, dolduruabilir ama boş bırakılabilir
 * bir alan olarak gösteriyor. Kural silinmedi, taşındı: `assertIdentity`
 * (`customers.service.ts`) artık kayıt anında bunu istemiyor, ama B2C fatura kesimi hâlâ
 * istiyor (`finance/invoice.readiness.ts`teki `dataRequirements()`) — kimliksiz bir müşteri
 * fatura aşamasına geldiğinde o ekran `BLOCKED` durumunu ve eksik alanı açıkça gösteriyor,
 * sessizce geçmiyor.
 *
 * Mükerrer kontrolü: telefon ya da e-posta yazıldıkça (350ms gecikmeyle) `GET
 * /customers/duplicates` çağrılıyor. Eşleşme varsa kaydet düğmesi kilitleniyor ve var olan kayda
 * giden bağlantı gösteriliyor; satışçı "bu farklı bir müşteri" onayını işaretlemeden ikinci bir
 * kayıt açamıyor. Sunucu da aynı kontrolü `create` sırasında tekrar yapıyor (409
 * `CUSTOMER_DUPLICATE`) — burası yalnız kaydetmeden önce uyarmak için, tek güvence değil.
 */
function HizliMusteriEkle({ git }: { git: (hedef: Yol) => void }) {
  const metin = KABUK_METIN.musteriler.hizliEkle;
  const [acik, setAcik] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<CustomerDuplicateProbe | null>(null);
  const [mukerrerYineDeKaydet, setMukerrerYineDeKaydet] = useState(false);

  useEffect(() => {
    if (!acik) return;
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();
    if (trimmedEmail.length < 5 && trimmedPhone.length < 6) { setDuplicates(null); return; }
    const timer = window.setTimeout(() => {
      void api
        .findDuplicateCustomers({ email: trimmedEmail || undefined, phone: trimmedPhone || undefined })
        .then((result) => setDuplicates(result.matches.length > 0 ? result : null))
        .catch(() => setDuplicates(null));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [acik, email, phone]);

  const sifirla = () => {
    setFirstName(''); setLastName(''); setPhone(''); setEmail(''); setNationalId('');
    setError(null); setDuplicates(null); setMukerrerYineDeKaydet(false);
  };

  if (!acik) {
    return (
      <button type="button" className="primary" data-testid="hizli-musteri-ekle-ac" onClick={() => setAcik(true)}>
        {metin.acDugmesi}
      </button>
    );
  }

  const mukerrerVar = duplicates !== null && duplicates.matches.length > 0;
  const kaydetKilitli = busy || (mukerrerVar && !mukerrerYineDeKaydet);

  const kaydet = async () => {
    setError(null);
    if (!firstName.trim() || !lastName.trim() || !phone.trim()) {
      setError(metin.dogrulamaZorunlu);
      return;
    }
    // TC Kimlik No artık zorunlu değil (Ürün Sahibi kararı, 26.08.2026); doluysa yine de
    // biçimi doğrulanıyor, çünkü yarım/yanlış bir numara boş numaradan kötü.
    const trimmedNationalId = nationalId.trim();
    if (trimmedNationalId && !/^[1-9]\d{10}$/.test(trimmedNationalId)) {
      setError(metin.dogrulamaTcKimlik);
      return;
    }
    const trimmedEmail = email.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError(metin.dogrulamaEposta);
      return;
    }
    setBusy(true);
    try {
      const created = await api.createCustomer({
        type: 'INDIVIDUAL',
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        email: trimmedEmail || undefined,
        nationalId: trimmedNationalId || undefined,
        // Durum kodu gönderilmiyor: sunucu tablodaki varsayılanı (isDefault) kullanıyor.
      });
      sifirla();
      setAcik(false);
      git(musteriYolu(created.id));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        const trimmedPhone = phone.trim();
        const refreshed = await api
          .findDuplicateCustomers({ email: trimmedEmail || undefined, phone: trimmedPhone || undefined })
          .catch(() => null);
        if (refreshed) setDuplicates(refreshed);
      }
      setError(caught instanceof ApiError ? caught.message : KABUK_METIN.calismaAlani.hata);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" data-testid="hizli-musteri-ekle" aria-labelledby="hizli-musteri-ekle-baslik">
      <div className="section-heading">
        <div>
          <h3 id="hizli-musteri-ekle-baslik">{metin.baslik}</h3>
          <p className="muted">{metin.tarif}</p>
        </div>
      </div>
      <Notices scope="hizli-musteri-ekle" error={error} notice={null} />
      {mukerrerVar && duplicates ? (
        <div className="callout" role="alert" data-testid="hizli-musteri-mukerrer">
          <div>
            <strong>{metin.mukerrerBaslik}</strong>
            <p>{metin.mukerrerTarif}</p>
            <ul>
              {duplicates.matches.map((match) => (
                <li key={match.id} data-testid="hizli-musteri-mukerrer-satiri">
                  <a href={musteriYolu(match.id)} onClick={baglantiTiklamasi(git, musteriYolu(match.id))}>
                    {match.displayName}
                  </a>
                  {' · '}{match.phone ?? ORTAK.belirtilmemis}{' · '}{match.email ?? ORTAK.belirtilmemis}
                  {' · '}<a href={musteriYolu(match.id)} onClick={baglantiTiklamasi(git, musteriYolu(match.id))}>{metin.mukerrerGoster}</a>
                </li>
              ))}
            </ul>
            <label>
              <input
                type="checkbox"
                data-testid="hizli-musteri-mukerrer-onay"
                checked={mukerrerYineDeKaydet}
                onChange={(event) => setMukerrerYineDeKaydet(event.target.checked)}
              />
              {' '}{metin.mukerrerYineDeKaydet}
            </label>
          </div>
        </div>
      ) : null}
      <AlanSatiri>
        <Alan etiket={metin.adEtiketi} zorunlu>
          <input data-testid="hizli-musteri-ad" value={firstName} maxLength={80} onChange={(event) => setFirstName(event.target.value)} />
        </Alan>
        <Alan etiket={metin.soyadEtiketi} zorunlu>
          <input data-testid="hizli-musteri-soyad" value={lastName} maxLength={80} onChange={(event) => setLastName(event.target.value)} />
        </Alan>
        <Alan etiket={metin.telefonEtiketi} zorunlu>
          <input data-testid="hizli-musteri-telefon" value={phone} maxLength={32} onChange={(event) => setPhone(event.target.value)} />
        </Alan>
        <Alan etiket={metin.epostaEtiketi} ipucu={metin.epostaIpucu}>
          <input data-testid="hizli-musteri-eposta" type="email" value={email} maxLength={320} onChange={(event) => setEmail(event.target.value)} />
        </Alan>
        <Alan etiket={metin.tcKimlikEtiketi} ipucu={metin.tcKimlikIpucu}>
          <input
            data-testid="hizli-musteri-tckimlik"
            value={nationalId}
            maxLength={11}
            inputMode="numeric"
            onChange={(event) => setNationalId(event.target.value.replace(/\D/g, ''))}
          />
        </Alan>
      </AlanSatiri>
      <div className="toolbar">
        <button type="button" className="primary" data-testid="hizli-musteri-kaydet" disabled={kaydetKilitli} onClick={() => void kaydet()}>
          {busy ? metin.kaydediliyor : metin.kaydet}
        </button>
        <button type="button" data-testid="hizli-musteri-vazgec" disabled={busy} onClick={() => { sifirla(); setAcik(false); }}>
          {metin.vazgec}
        </button>
      </div>
    </section>
  );
}

function CustomersPage({ me }: { me: Me }) {
  const [tab, setTab] = useState<'customers' | 'conversations'>(() => new URLSearchParams(window.location.search).has('konusma') ? 'conversations' : 'customers');
  return <>
    {me.permissions.includes('conversation:read') ? <div className="customer-tabs" role="tablist" aria-label={KABUK_METIN.musteriler.baslik}>
      <button type="button" role="tab" aria-selected={tab === 'customers'} onClick={() => setTab('customers')}>{YAZISMALAR_METIN.customers}</button>
      <button type="button" role="tab" aria-selected={tab === 'conversations'} onClick={() => setTab('conversations')}>{YAZISMALAR_METIN.tab}</button>
    </div> : null}
    {tab === 'conversations' && me.permissions.includes('conversation:read') ? <Yazismalar me={me} /> : <CustomerListPage me={me} />}
  </>;
}

function CustomerListPage({ me }: { me: Me }) {
  const { git } = useYol();
  const [customers, setCustomers] = useState<CustomerList | null>(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const metin = KABUK_METIN.musteriler;
  const totalPages = Math.max(1, Math.ceil((customers?.total ?? 0) / (customers?.pageSize ?? 10)));

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setCustomers(await api.customers({ q: query.trim(), page, pageSize: 10, sort: 'displayName', direction: 'asc' })); }
    catch (caught) { setError(caught instanceof ApiError ? caught.message : KABUK_METIN.calismaAlani.hata); }
    finally { setLoading(false); }
  }, [query, page]);
  useEffect(() => { const timer = window.setTimeout(() => void load(), 220); return () => window.clearTimeout(timer); }, [load]);

  return (
    <>
      {error ? <div className="error callout" role="alert" data-testid="workspace-error"><span>{error}</span><button type="button" onClick={() => void load()}>{ORTAK.yeniden}</button></div> : null}
      <section id="customers" className="content-section" aria-labelledby="customers-title">
        <div className="section-heading"><div><span className="eyebrow">{metin.ustBaslik}</span><h2 id="customers-title">{metin.baslik}</h2></div><span className="count-badge">{tamSayi(customers?.total ?? 0)} {ORTAK.kayit}</span></div>
        <div className="toolbar">
          <label className="search-field"><span>{metin.araBaslik}</span><input data-testid="customer-search" placeholder={metin.araIpucu} value={query} maxLength={80} onChange={(event) => { setPage(1); setQuery(event.target.value); }} /></label>
          {me.permissions.includes('customer:create') ? <HizliMusteriEkle git={git} /> : null}
        </div>
        <div className="table-wrap">
          <table data-testid="customer-table">
            <thead><tr><th>{metin.sutunAd}</th><th>{metin.sutunTur}</th><th>{metin.sutunDurum}</th><th>{metin.sutunEposta}</th>{me.permissions.includes('customer:read_financial') ? <th>{metin.sutunKrediLimiti}</th> : null}</tr></thead>
            {/*
              Kredi limiti TL varsayılmıyor: tutarın yanındaki `billingCurrencyCode` ne diyorsa
              para birimi o. Bugünkü verinin tamamı EUR (`docs/tr-sozluk.md`).
            */}
            {/*
              Satır tıklanıyor: bugüne kadar bir müşteriye tıklamanın hiçbir karşılığı yoktu,
              telefon, not ve sipariş geçmişi hiçbir yerde görünmüyordu. Satırın kendisi de
              (fare için, geniş hedef) `onClick` taşıyor, ama gerçek erişilebilirlik adın
              içindeki `<a href>`den geliyor: klavye, ekran okuyucu, yeni sekmede açma hep çalışır.
            */}
            <tbody>{customers?.items.map((customer) => <tr key={customer.id} data-testid="customer-row" className="row-link" onClick={() => git(musteriYolu(customer.id))}><td><a href={musteriYolu(customer.id)} aria-label={metin.satiriAc(customer.displayName)} onClick={baglantiTiklamasi(git, musteriYolu(customer.id))}>{customer.displayName}</a></td><td>{musteriTuru(customer.type)}</td><td><span className={`state state-${customer.statusCode.toLowerCase()}`}>{musteriDurumu(customer.statusCode)}</span></td><td>{customer.email ?? ORTAK.belirtilmemis}</td>{me.permissions.includes('customer:read_financial') ? <td data-testid="customer-credit">{para(customer.financial?.creditLimitMinor, customer.financial?.billingCurrencyCode)}</td> : null}</tr>)}</tbody>
          </table>
          {!loading && customers?.items.length === 0 ? <div className="empty-state"><strong>{metin.bosBaslik}</strong><span>{metin.bosTarif}</span></div> : null}
        </div>
        <div className="pagination"><p data-testid="customer-total">{customers ? `${tamSayi(customers.total)} ${metin.sayfaEki} · ${ORTAK.sayfa} ${tamSayi(customers.page)}/${tamSayi(totalPages)}` : metin.yukleniyor}</p><div><button type="button" disabled={page <= 1 || loading} onClick={() => setPage((current) => Math.max(1, current - 1))}>{ORTAK.onceki}</button><button type="button" data-testid="next-page" disabled={page >= totalPages || loading} onClick={() => setPage((current) => current + 1)}>{ORTAK.sonraki}</button></div></div>
      </section>
    </>
  );
}

/** Bir bölümün sayfası: o bölümün panosu, altında o bölümün çalışma paneli. */
function SectionPage({ me, sections, bolum }: { me: Me; sections: DashboardSectionIndex | null; bolum: string }) {
  if (bolum === 'OPERATIONS') return <OperationsWorkspace me={me} />;
  if (bolum === 'SALES') return <SalesWorkspace me={me} sections={sections} />;
  if (bolum === 'FINANCE') return <FinanceWorkspace me={me} />;
  if (bolum === 'MARKETING') return <PazarlamaSekmeleri me={me} sections={sections} />;
  return (
    <div className="workflow-stack">
      <DashboardPanels sections={sections} only={bolum} />
      {/* İK (30.08.2026): beşinci bölümün çalışma paneli, çalışan dizini. */}
      {bolum === 'HR' ? <IkPaneli me={me} /> : null}
    </div>
  );
}

/** Bölüm anahtarı ile adresi arasındaki eşleme. Uç bölüm adını gönderiyor, adres bizim. */
const BOLUM_YOLU: Record<string, Yol> = {
  SALES: YOLLAR.satis,
  OPERATIONS: YOLLAR.operasyon,
  FINANCE: YOLLAR.finans,
  MARKETING: YOLLAR.pazarlama,
  HR: YOLLAR.ik,
};

function Workspace({ me, onSignedOut }: { me: Me; onSignedOut: () => void }) {
  const [sections, setSections] = useState<DashboardSectionIndex | null>(null);
  // P1-6 (denetim, 07.09.2026): istek başarısız olduğunda `sections` sessizce `null` kalıyordu —
  // bu, "rol hiçbir bölüm görmüyor" durumuyla aynı görünümdü. Artık ikisi ayrılıyor: `null` hâlâ
  // "henüz yüklenmedi ya da rolün bölümü yok" demek, `sectionsError` "yükleme başarısız oldu,
  // yeniden dene" demek.
  const [sectionsError, setSectionsError] = useState<string | null>(null);
  const { yol, git } = useYol();
  const theme = ROL_CALISMA_ALANI[me.role] ?? { baslik: KABUK_METIN.calismaAlani.varsayilanBaslik, tarif: '' };

  const loadSections = useCallback(() => {
    setSectionsError(null);
    api
      .dashboardSections()
      .then(setSections)
      .catch((caught: unknown) => {
        setSections(null);
        setSectionsError(caught instanceof ApiError ? caught.message : KABUK_METIN.gezinme.bolumlerYuklenemedi);
      });
  }, []);
  useEffect(() => { loadSections(); }, [loadSections]);
  useEffect(() => seridiIzle(), []);

  /**
   * Menüden gidildiğinde menü kapanıyor.
   *
   * `popover` dışına tıklamayı ve `Esc`i kendisi hallediyor, ama **kendi içindeki** bir bağlantı
   * sayfayı değiştirmiyor (adres çubuğuna gitmiyoruz, React yeniden çiziyor), o yüzden menü
   * açık kalıyordu. Kapatmayı tarayıcıya söylemek bir satır; menüyü baştan yazmak yüz satır.
   */
  const hesapMenusu = useRef<HTMLDivElement>(null);

  const menudenGit = (hedef: Yol) => (olay: React.MouseEvent<HTMLAnchorElement>) => {
    hesapMenusu.current?.hidePopover();
    baglantiTiklamasi(git, hedef)(olay);
  };

  // P1-6 (denetim, 07.09.2026): çıkış isteğinin hiç `.catch()`i yoktu. Sunucu 503 ile kesilince
  // (ör. bakım penceresi) düğme hiçbir şey söylemiyordu; ekran açık kalıyor, kullanıcı tekrar
  // tekrar tıklıyordu. Artık hata menüde görünür kalıyor ve menü açık kalıyor ki kullanıcı onu
  // kaçırmasın.
  const [cikisHata, setCikisHata] = useState<string | null>(null);
  const [cikisMesgul, setCikisMesgul] = useState(false);
  const cikisYap = () => {
    setCikisMesgul(true);
    setCikisHata(null);
    api
      .logout()
      .then(onSignedOut)
      .catch((caught: unknown) => {
        setCikisHata(caught instanceof ApiError ? caught.message : KABUK_METIN.hesap.cikisHata);
      })
      .finally(() => setCikisMesgul(false));
  };

  /**
   * Menü.
   *
   * Her kavram **bir kez** geçiyor. Eskiden dört pano bölümünün yanına dört akış bağlantısı daha
   * konmuştu ve satış yetkisi olan bir kullanıcı menüde hem "Satış" hem "Sales" görüyordu.
   * Akış panelleri kendi bölümlerinin sayfasına taşındı, "Oversight" ise "Denetim" adıyla
   * bölümlerin üstünde duran ayrı bir sayfa oldu.
   *
   * Sekmeyi gizlemek yetkilendirme değil: arkadaki her uç sunucuda yeniden kontrol ediliyor.
   * Burada gizlenen şey yalnız kullanılamayacak bir düğme.
   */
  const menu: MenuMaddesi[] = useMemo(() => {
    const maddeler: (MenuMaddesi & { goster: boolean })[] = [
      { yol: YOLLAR.genelBakis, etiket: KABUK_METIN.gezinme.genelBakis, goster: true },
      { yol: YOLLAR.turBilgi, etiket: TUR_BILGI_METIN.baslik, goster: true },
      // Otel teklifi: oda fiyatı sorar ve müşteriye giden fiyat belgesi üretir; uçlar `sales:write` ister.
      { yol: YOLLAR.otelTeklif, etiket: OTEL_TEKLIF_METIN.baslik, goster: me.permissions.includes('sales:write') },
      // Hızlı teklif: otel fiyatı sorar ve müşteriye giden çıktı üretir; uçlar `sales:write` ister.
      { yol: YOLLAR.hizliTeklif, etiket: HIZLI_METIN.menu, goster: me.permissions.includes('sales:write') },
      /*
       * Müşteri listesi izne bağlandı (30.08.2026, İK bölümü eklenirken ölçüldü).
       *
       * Bu satır `goster: true` idi ve o gün doğruydu: `customer:read` iznini bütün roller
       * tutuyordu. Beşinci bölümün rolü (HR) onu tutmuyor — İK müşteriyle çalışmıyor — ve bağlantı
       * ona da çiziliyordu. Tıklayan bir hata ekranı alıyordu: uç 403 döndürüyor.
       *
       * Var olan hiçbir rol için bir şey değişmiyor; hepsi `customer:read` tutuyor. Değişen şey,
       * tıklanınca reddedilecek bir kapının artık çizilmemesi.
       */
      { yol: YOLLAR.musteriler, etiket: KABUK_METIN.gezinme.musteriler, goster: me.permissions.includes('customer:read') },
      ...(sections?.sections ?? []).flatMap((section) => {
        const hedef = BOLUM_YOLU[section.key];
        if (hedef === undefined) return [];
        return [{ yol: hedef, etiket: BOLUM_ADI[section.key] ?? section.key, goster: true, ...(section.badgeLate > 0 ? { sayi: section.badgeLate } : {}) }];
      }),
      // PDF Düzenle işleme izni olup Operasyon bölümünü görmeyen rol (satışçı): Operasyon sayfası yalnız bu eylemle açılır.
      { yol: YOLLAR.operasyon, etiket: BOLUM_ADI.OPERATIONS ?? 'Operasyon', goster: sections !== null && me.permissions.includes('tour-pdf:process') && !sections.sections.some((section) => section.key === 'OPERATIONS') },
      { yol: YOLLAR.denetim, etiket: KABUK_METIN.gezinme.denetim, goster: me.permissions.includes('oversight:read') },
      { yol: YOLLAR.v3, etiket: KABUK_METIN.gezinme.v3, goster: me.permissions.includes('web_intake:read') || (me.permissions.includes('finance:read') && me.permissions.includes('commerce:finance_read')) },
      { yol: YOLLAR.belgeler, etiket: KABUK_METIN.gezinme.belgeler, goster: me.permissions.includes('document:read') },
      { yol: YOLLAR.tedarikciler, etiket: KABUK_METIN.gezinme.tedarikciler, goster: me.permissions.includes('supplier:read') },
      /*
        Kullanıcılar üst şeritten kalktı (Ürün Sahibi, 10.09.2026) ve hesap menüsüne taşındı:
        Profil ile Ayarlar zaten oradaydı, "kim, hangi rolde, hesabı açık mı" sorusu da bir hesap
        işi. Kapı ve yetki koşulu aynı kaldı (`user:read`), sadece render yeri değişti — bkz.
        `hesap-menu` içindeki bağlantı.
      */
      /*
        Yapılandırma menüde yalnız yazabilene görünüyor. Okuma yetkisi (`config:read`) her rolde
        var ama okunacak tek şey bir sistem yöneticisinin yazdığı ayar; salt okunur bir sekme,
        beş rolün menüsüne hiçbir işi olmayan bir kapı eklemek olurdu.
      */
      { yol: YOLLAR.yapilandirma, etiket: KABUK_METIN.gezinme.yapilandirma, goster: me.permissions.includes('config:write') },
    ];
    return maddeler.filter((madde) => madde.goster).map(({ yol: hedef, etiket, sayi }) => ({ yol: hedef, etiket, ...(sayi ? { sayi } : {}) }));
  }, [me.permissions, sections]);

  const musteriId = musteriKimligi(yol);
  const siparisId = siparisKimligi(yol);

  const sayfa = () => {
    if (musteriId) return <CustomerDetailPage id={musteriId} me={me} />;
    if (siparisId) return <OrderDetailPage id={siparisId} me={me} sections={sections} />;
    switch (yol) {
      case YOLLAR.turBilgi: return <TurBilgi />;
      case YOLLAR.otelTeklif: return <OtelTeklif />;
      case YOLLAR.hizliTeklif: return <HizliTeklif musteriEkleyebilir={me.permissions.includes('customer:create')} />;
      case YOLLAR.musteriler: return <CustomersPage me={me} />;
      case YOLLAR.satis: return <SectionPage me={me} sections={sections} bolum="SALES" />;
      case YOLLAR.belgeler: return <Belgeler canWrite={me.permissions.includes('document:write')} />;
      case YOLLAR.operasyon: return <SectionPage me={me} sections={sections} bolum="OPERATIONS" />;
      case YOLLAR.finans: return <SectionPage me={me} sections={sections} bolum="FINANCE" />;
      case YOLLAR.pazarlama: return <SectionPage me={me} sections={sections} bolum="MARKETING" />;
      case YOLLAR.ik: return <SectionPage me={me} sections={sections} bolum="HR" />;
      case YOLLAR.denetim: return <div className="workflow-stack"><OversightWorkflowPanel me={me} /></div>;
      case YOLLAR.v3: return <WebSitesi me={me} />;
      case YOLLAR.tedarikciler: return <div className="workflow-stack"><SuppliersPage me={me} /></div>;
      case YOLLAR.kullanicilar: return <div className="workflow-stack"><UsersPage me={me} /></div>;
      case YOLLAR.yapilandirma: return <YapilandirmaSayfasi me={me} />;
      case YOLLAR.profil: return <ProfilePage me={me} />;
      case YOLLAR.ayarlar: return <SettingsPage />;
      default: return <OverviewPage me={me} />;
    }
  };

  /**
   * Ekranın adı: `h1`.
   *
   * Açılış sayfasında rolün çalışma alanının adı yazıyor, çünkü orası o rolün karşılandığı yer
   * ve altı rol altı ayrı ekran görüyor. Bölüm sayfalarında ise bulunulan bölümün adı yazıyor:
   * bir sayfanın adı, o sayfada ne olduğu olmalı.
   */
  const acikMadde = menu.find((madde) => madde.yol === yol);
  const sayfaBasligi =
    yol === YOLLAR.genelBakis
      ? theme.baslik
      : yol === YOLLAR.profil
        ? HESAP_METIN.profil.baslik
        : yol === YOLLAR.ayarlar
          ? HESAP_METIN.ayarlar.baslik
          : yol === YOLLAR.kullanicilar
            ? KABUK_METIN.gezinme.kullanicilar
            : yol === YOLLAR.hizliTeklif
              ? HIZLI_METIN.baslik
              : musteriId
              ? KABUK_METIN.musteriDetay.baslik
              : siparisId
                ? KABUK_METIN.siparisDetay.baslik
                : (acikMadde?.etiket ?? theme.baslik);

  return (
    <div className="app-shell">
      {/* CRM-A14: girişte rolün tek bölümüne düşer, `Dashboard.tsx` içindeki gerekçeye bakın. */}
      <GirisYonlendirmesi sections={sections} />
      {/*
        Üst şerit kaydırınca gizleniyor; sol menü ayrı konumlanıyor. Yapı v3'ün üst şeridiyle aynı ikili
        (`src/ui/chrome.ts`): üstte turkuaz kullanım şeridi, altında beyaz ana şerit.
      */}
      <header className="topbar">
        <div className="topbar__utility">
          <div>
            <span>{KABUK_METIN.marka.serit}</span>
            {/* Aynı ölçüm burada da geçerli: bkz. giriş ekranındaki not. */}
            {import.meta.env.DEV ? (
              <span><span className="status-dot" /> {KABUK_METIN.ust.ortam}</span>
            ) : null}
          </div>
        </div>
        <div className="topbar__main">
          {/*
            Gerçek logo. İlk turda "dosya yok" diye raporlanmıştı; dosya v2'nin sunucusundaydı,
            şimdi v3'ün `assets/brand/` dizininde ve `npm run marka:getir` onu 113,5 KB'dan
            40,6 KB'a indirerek buraya yazıyor. Karanlık kipte altına açık bir zemin konuyor,
            logo boyanmıyor: gerekçesi `styles.css` içinde ölçümüyle yazılı.
          */}
          <a className="brand" href={YOLLAR.genelBakis} onClick={baglantiTiklamasi(git, YOLLAR.genelBakis)}>
            <img className="marka-logo" src={logoAdresi} alt={KABUK_METIN.marka.logoAlt} width={128} height={60} />
          </a>
          {sectionsError ? (
            <div className="error callout" role="alert" data-testid="sections-error">
              <span>{sectionsError}</span>
              <button type="button" onClick={loadSections}>{ORTAK.yeniden}</button>
            </div>
          ) : null}
          <div className="account-card">
            <BildirimZili key={me.userId} userId={me.userId} git={git} />
            {/*
              Hesap menüsü betiksiz: `popover` özniteliği `Esc` ile kapanmayı, dışına tıklayınca
              kapanmayı ve odak yönetimini tarayıcıdan alıyor.
            */}
            <button
              type="button"
              className="hesap-dugmesi"
              data-testid="hesap-dugmesi"
              popoverTarget="hesap-menu"
              aria-label={KABUK_METIN.hesap.ac}
            >
              <span className="avatar" aria-hidden="true">{me.displayName.slice(0, 1).toLocaleUpperCase(YEREL)}</span>
              <span><strong>{me.displayName}</strong><small>{rolAdi(me.role)}</small></span>
            </button>
            <div className="hesap-menu" id="hesap-menu" data-testid="hesap-menu" popover="auto" ref={hesapMenusu}>
              <span className="hesap-menu-baslik"><strong>{me.displayName}</strong><small>{rolAdi(me.role)}</small></span>
              <a href={YOLLAR.profil} data-testid="menu-profil" onClick={menudenGit(YOLLAR.profil)}>{KABUK_METIN.hesap.profil}</a>
              <a href={YOLLAR.ayarlar} data-testid="menu-ayarlar" onClick={menudenGit(YOLLAR.ayarlar)}>{KABUK_METIN.hesap.ayarlar}</a>
              {/*
                Kullanıcılar (Ürün Sahibi, 10.09.2026): üst şeritten kalkıp hesap menüsüne geldi,
                Ayarlar'ın altında Çıkış'ın üstünde. Kapı koşulu üst şerittekiyle aynıydı
                (`user:read`), burada da aynı kaldı.
              */}
              {me.permissions.includes('user:read') ? (
                <a
                  href={YOLLAR.kullanicilar}
                  data-testid="menu-kullanicilar"
                  aria-current={yol === YOLLAR.kullanicilar ? 'page' : undefined}
                  onClick={menudenGit(YOLLAR.kullanicilar)}
                >
                  {KABUK_METIN.gezinme.kullanicilar}
                </a>
              ) : null}
              <button type="button" data-testid="logout" disabled={cikisMesgul} onClick={cikisYap}>{KABUK_METIN.hesap.cikis}</button>
              {cikisHata ? (
                <p className="error" role="alert" data-testid="logout-error">{cikisHata}</p>
              ) : null}
            </div>
          </div>
        </div>
      </header>
      <SolMenu menu={menu} izinler={me.permissions} yol={yol} git={git} />
      <div className="workspace-column">
        <header className={`workspace${yol === YOLLAR.turBilgi || yol === YOLLAR.otelTeklif || yol === YOLLAR.hizliTeklif ? ' tb-workspace' : ''}`} data-testid="workspace-header">
          {/*
            Rozetin içi **kod değeri** olarak kalıyor (`ADMIN`): testler ona bakıyor ve kod değeri
            veritabanındaki değerin ta kendisi. Üstteki yazı ve başlık ise insanın okuduğu ad.
          */}
          <div>
            <span className="eyebrow">{rolAdi(me.role).toLocaleUpperCase(YEREL)} {KABUK_METIN.calismaAlani.ustBaslikEki}</span>
            <h1 data-testid="workspace-title">{sayfaBasligi}</h1>
            {/*
              Rolün tarifi yalnız açılış sayfasında. Bölüm sayfalarında o cümle bulunduğu
              ekranla ilgisiz kalıyordu: Operasyon sayfasının altında "Kullanıcılar,
              yapılandırma ve denetim izi" yazıyordu.
            */}
            {yol === YOLLAR.genelBakis ? <p>{theme.tarif}</p> : null}
          </div>
          <span className="role-pill" data-testid="workspace-role"><span className="status-dot" /> {me.role}</span>
        </header>
        <main className="workspace-main">
          {/*
            Bölüm sınırı `key={yol}` ile her sayfa geçişinde yeniden kuruluyor: bir bölüm
            çöktükten sonra menüden başka bir sayfaya gidip geri dönmek çökmüş durumda takılı
            kalmıyor, temiz bir bölüm baştan çiziliyor. Üst şerit ve menü bu sınırın dışında.
          */}
          <HataSiniri seviye="bolum" bolumAdi={sayfaBasligi} key={yol}>
            <HataTetikleyici seviye="bolum" />
            {sayfa()}
          </HataSiniri>
        </main>
      </div>
    </div>
  );
}

export function App() {
  // Parola belirleme sayfası oturumsuz: bağlantıyı alan kişinin henüz parolası yok.
  if (window.location.pathname === PAROLA_KUR_YOLU) {
    return <HataSiniri seviye="uygulama"><ParolaKurPage /></HataSiniri>;
  }
  return <AnaUygulama />;
}

function AnaUygulama() {
  const [me, setMe] = useState<Me | null>(null);
  const [checked, setChecked] = useState(false);
  useEffect(() => { api.me().then(setMe).catch(() => setMe(null)).finally(() => setChecked(true)); }, []);
  useLayoutEffect(() => {
    if (!me) return;
    return subscribeSessionExpiry(() => setMe(null));
  }, [me]);
  return (
    <HataSiniri seviye="uygulama">
      <HataTetikleyici seviye="uygulama" />
      {!checked ? (
        <main className="boot-screen" data-testid="loading"><span className="spinner" />{KABUK_METIN.acilis.yukleniyor}</main>
      ) : me ? (
        <Workspace me={me} onSignedOut={() => setMe(null)} />
      ) : (
        <LoginForm onSignedIn={setMe} />
      )}
    </HataSiniri>
  );
}
